import { Tooltip } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import DeleteOutlineOutlined from '@material-ui/icons/DeleteOutlineOutlined';
import Alert from '@material-ui/lab/Alert';
import { FieldArray, useFormikContext } from 'formik';
import * as React from 'react';
import CurrencyFormField from 'v2/components/Shared/FormFields/CurrencyFormField';
import { OfferCurrencyAlert } from 'v2/components/Shared/OfferCurrencyAlert';
import { Space } from 'v2/components/Shared/Space';
import { getCurrencySymbol } from 'v2/helpers/getCurrencySymbol';
import { useCurrencies } from 'v2/hooks/useCurrencies';

import type { Currency } from 'v2/interfaces/currency';

import { ChooseCurrenciesModal } from './ChooseCurrenciesModal';
import { useStyles } from './styles';

import type { FormValues, Prices } from '../constants';

type PricingProps = {
  prices: Prices;
};

function Pricing({ prices = [] }: PricingProps) {
  const { values } = useFormikContext<FormValues>();
  const [isChooseCurrenciesModalOpen, setIsChooseCurrenciesModalOpen] = React.useState(false);
  const [selectedCurrencies, setSelectedCurrencies] = React.useState<Currency[]>([]);
  const currenciesQuery = useCurrencies();

  const classes = useStyles();

  function addCurrencies(currencies: Currency[]) {
    setSelectedCurrencies([...selectedCurrencies, ...currencies]);
  }

  function removeCurrency(currencyId: number) {
    setSelectedCurrencies(selectedCurrencies.filter((selectedCurrency) => selectedCurrency.id !== currencyId));
  }

  React.useEffect(() => {
    if (!prices.length) return;
    if (!currenciesQuery.data) return;
    const currencies = currenciesQuery.data;
    const foreignPrices = prices.filter((price) => price.currency_code !== 'BRL');
    const selectedCurrencies: Currency[] = foreignPrices.map((price) => {
      return currencies.find((currency) => currency.code === price.currency_code);
    });
    setSelectedCurrencies(selectedCurrencies);
  }, [prices, currenciesQuery.data]);

  return (
    <>
      <Typography variant='h6'>Precificação</Typography>

      <FieldArray name='prices'>
        {({ push, remove }) => (
          <>
            <Paper style={{ padding: 16 }} elevation={0}>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableCell width={200}>Moeda</TableCell>
                    <TableCell width={200}>Valor</TableCell>
                    {!values.membership_fee && <TableCell>&nbsp;</TableCell>}
                    {values.membership_fee && <TableCell>Taxa de contratação / adesão</TableCell>}
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className={classes.currencyCell}>BRL (Real Brasileiro)</TableCell>
                      <TableCell>
                        <CurrencyFormField
                          id='plan-price-BRL'
                          name='prices.0.value'
                          InputProps={{
                            startAdornment: <InputAdornment position='start'>R$</InputAdornment>,
                          }}
                          inputProps={{
                            maxLength: 20,
                            style: {
                              width: 100,
                            },
                          }}
                        />
                      </TableCell>
                      {!values.membership_fee && <TableCell id='empty_cell' />}
                      {values.membership_fee && !values.trial && (
                        <TableCell>
                          <CurrencyFormField
                            id='membership_value'
                            name='prices.0.membership_value'
                            InputProps={{
                              startAdornment: <InputAdornment position='start'>R$</InputAdornment>,
                            }}
                            inputProps={{
                              maxLength: 20,
                              style: {
                                width: 100,
                              },
                            }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Space size={6} axis='vertical' />
              <Alert severity='info'>É obrigatória a configuração do valor em real para a conversão automática de suas vendas.</Alert>
              <Space size={32} axis='vertical' />
              <div className={classes.cardHeader}>
                <Typography>Cadastre valores diferenciados para moedas internacionais</Typography>
                <Button id='add-currency-button' variant='outlined' onClick={() => setIsChooseCurrenciesModalOpen(true)}>
                  Cadastrar novo valor
                </Button>
              </div>
              {!!selectedCurrencies.length && (
                <>
                  <Space size={16} axis='vertical' />
                  <TableContainer>
                    <Table size='small'>
                      <TableHead>
                        <TableCell width={200}>Moeda</TableCell>
                        <TableCell width={200}>Valor</TableCell>
                        {values.membership_fee && !values.trial && <TableCell width={200}>Taxa de contratação / adesão</TableCell>}
                        <TableCell align='right' style={{ paddingRight: 20 }}>
                          Ações
                        </TableCell>
                      </TableHead>
                      <TableBody>
                        {selectedCurrencies.map((row, index) => (
                          <TableRow key={row.id}>
                            <TableCell className={classes.currencyCell}>
                              {row.code} ({row.name})
                            </TableCell>
                            <TableCell>
                              <CurrencyFormField
                                id={`plan-price-${row.code}`}
                                name={`prices.${index + 1}.value`}
                                currency={row.code}
                                inputProps={{
                                  maxLength: 20,
                                  style: {
                                    width: 100,
                                  },
                                }}
                                InputProps={{
                                  startAdornment: <InputAdornment position='start'>{getCurrencySymbol('pt-BR', row.code)}</InputAdornment>,
                                }}
                              />
                            </TableCell>
                            {values.membership_fee && !values.trial && (
                              <TableCell>
                                <CurrencyFormField
                                  id={`prices.${row.code}.membership_value`}
                                  name={`prices.${index + 1}.membership_value`}
                                  currency={row.code}
                                  InputProps={{
                                    startAdornment: <InputAdornment position='start'>{getCurrencySymbol('pt-BR', row.code)}</InputAdornment>,
                                  }}
                                  inputProps={{
                                    maxLength: 20,
                                    style: {
                                      width: 100,
                                    },
                                  }}
                                />
                              </TableCell>
                            )}
                            <TableCell align='right'>
                              <Tooltip title='Remover' placement='top' arrow>
                                <IconButton
                                  id={`remove-currency-button-${row.code}`}
                                  onClick={() => {
                                    remove(index + 1);
                                    removeCurrency(row.id);
                                  }}
                                >
                                  <DeleteOutlineOutlined />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Space size={16} axis='vertical' />
                  <OfferCurrencyAlert />
                </>
              )}
            </Paper>
            <ChooseCurrenciesModal
              isOpen={isChooseCurrenciesModalOpen}
              closeModal={() => setIsChooseCurrenciesModalOpen(false)}
              selectedCurrencies={selectedCurrencies}
              addCurrencies={(currencies) => {
                currencies.forEach((currency) =>
                  push({
                    value: 0,
                    membership_value: 0,
                    currency_code: currency.code,
                  })
                );
                addCurrencies(currencies);
              }}
            />
          </>
        )}
      </FieldArray>
    </>
  );
}

export { Pricing };
